const helpSections = [
  // ==== INTRO ====
  {
    id: "getting-started",
    title: "Getting Started",
    icon: "rocket",
    category: "help",
    description: "Overview of how the Plutonic virtual therapy platform works.",
    video: "",

    content: [
      {
        type: "header",
        text: "Overview",
      },
      {
        type: "text",
        text: "Plutonic’s virtual therapy system includes multiple immersive spaces where therapists and clients can interact in real time. Sessions are manually scheduled and each space opens 15 minutes before and after the session.",
      },

      {
        type: "header",
        text: "Requirements",
      },
      {
        type: "text",
        text: "• Stable internet connection\n• Google Chrome or Microsoft Edge browser\n• Desktop or laptop (mobile not supported)",
      },

      {
        type: "header",
        text: "Virtual Space Features",
      },
      {
        type: "text",
        text: "• Voice chat\n• Message chat\n• Video conferencing\n• Screenshare\n• Emoting animations\n• Whiteboard & Cognitive Excercises\n• Themed Virtual Spaces\n• Interactable Items",
      },

      {
        type: "image",
        src: "https://react-resources.s3.us-east-1.amazonaws.com/helpCenterImages/plutonicOverview.png",
      },

      {
        type: "header",
        text: "Menu Overview",
      },

      {
        type: "header",
        text: "1. Avatar Creation",
      },
      {
        type: "text",
        text: "Plutonic has it's own custom avatar system for you to customize and express yourself. Use this with your clients to help resonate with them.",
      },
      {
        type: "image",
        src: "https://react-resources.s3.us-east-1.amazonaws.com/helpCenterImages/avatar.png",
      },

      {
        type: "header",
        text: "2. Whiteboard",
      },
      {
        type: "text",
        text: "The whiteboard allows you to collaborate with clients. You can request to upload worksheets and work on cognitive exercises with your clients.",
      },
      {
        type: "image",
        src: "https://react-resources.s3.us-east-1.amazonaws.com/helpCenterImages/whiteboard.png",
      },

      {
        type: "header",
        text: "3. Settings",
      },
      {
        type: "text",
        text: "Most users won’t need to adjust settings. However, you should monitor FPS (frames per second). Ideally, it should stay above 30 for smooth performance.",
      },
      {
        type: "image",
        src: "https://react-resources.s3.us-east-1.amazonaws.com/helpCenterImages/settings.png",
      },

      {
        type: "header",
        text: "4. Controls",
      },
      {
        type: "text",
        text: "Movement and interaction controls are intuitive and displayed in the interface. These allow you to navigate and interact with the environment.",
      },
      {
        type: "image",
        src: "https://react-resources.s3.us-east-1.amazonaws.com/helpCenterImages/controls.png",
      },

      {
        type: "header",
        text: "5. Environment Settings",
      },
      {
        type: "text",
        text: "You can adjust the time of day (morning, afternoon, evening). Lighting and ambient sounds will change accordingly.",
      },

      {
        type: "image",
        src: "https://react-resources.s3.us-east-1.amazonaws.com/helpCenterImages/environment.png",
      },

      {
        type: "header",
        text: "6. Leaving a Session",
      },
      {
        type: "text",
        text: "To leave a session, either close your browser or open the menu and confirm exit.",
      },

      {
        type: "image",
        src: "https://react-resources.s3.us-east-1.amazonaws.com/helpCenterImages/leaveSession.png",
      },
    ],
  },


  // ==== SCHEDULING ====
  {
    id: "sessions",
    title: "Scheduling Sessions",
    icon: "calendar",
    category: "help",
    description: "How to create and update sessions.",
    video: "https://react-resources.s3.us-east-1.amazonaws.com/helpCenterVideos/SchedulingTherapists.mp4", // optional
    content: [
      { type: "text", text: "" },
    ],
  },

  // ==== FIRST SESSION ====
  {
    id: "firstSession",
    title: "First Session & Setup",
    icon: "user",
    category: "help",
    description: "Set up process and your first session.",
    video: "https://react-resources.s3.us-east-1.amazonaws.com/helpCenterVideos/firstSessionSetup.mkv", // optional
    content: [
      { type: "Header", text: "First Session" },
      { type: "Text", text: "Watch the video above for the set up proccess and best practices when starting your first session. In addition, below are some helpful session starters you can consider incorporating to take full advantage of your virtual space." },
      {
        type: "header",
        text: "Whiteboard-Friendly Session Starters",
      },
      {
        type: "text",
        text: "These activities help ease into sessions, build comfort, and encourage engagement using the whiteboard.",
      },

      {
        type: "header",
        text: "1. Draw Your Mood",
      },
      {
        type: "text",
        text: "Ask the client to draw how they’re feeling instead of describing it. This can be a simple doodle like a smiley face, storm cloud, or sun. It’s a low-pressure way to begin emotional check-ins.",
      },
      {
        type: "image",
        src: "https://react-resources.s3.us-east-1.amazonaws.com/helpCenterImages/drawMood.png",
      },

      {
        type: "header",
        text: "2. Emoji / Symbol Poll",
      },
      {
        type: "text",
        text: "Draw a few simple symbols (like a sun, rain cloud, fire, or tree) and ask the client which one represents their current mood. This gives a quick emotional snapshot.",
      },

      {
        type: "header",
        text: "3. Two Truths and a Lie",
      },
      {
        type: "text",
        text: "Write three short statements on the whiteboard—two true and one false. Have the client guess which is the lie. This builds rapport while keeping things light.",
      },

      {
        type: "header",
        text: "4. Rose, Thorn, Bud",
      },
      {
        type: "text",
        text: "Divide the whiteboard into three sections: Rose (something positive), Thorn (a challenge), and Bud (something to look forward to). Both therapist and client can contribute.",
      },
      {
        type: "image",
        src: "https://react-resources.s3.us-east-1.amazonaws.com/helpCenterImages/roseThornBud.png",
      },

      {
        type: "header",
        text: "5. High / Low of the Week",
      },
      {
        type: "text",
        text: "Create two columns labeled High Point and Low Point. Have the client add notes for each. This helps transition into deeper discussion and reflection.",
      },
    ],
  },

  // ==== Quicklink ====
  {
    id: "quicklink",
    title: "Session Quicklink",
    category: "help",
    icon: "link",
    description: "Learn how to create quicklinks, an easy and quick way to meet with clients. No registration required.",
    video: "https://react-resources.s3.us-east-1.amazonaws.com/helpCenterVideos/quicklinks.mkv", // optional
    content: [
      { type: "text", text: "" },
    ],
  },


  // ==== ONBOARDING ====
  {
    id: "client-onboarding",
    title: "Client Onboarding",
    category: "help",
    icon: "clipboard",
    description: "Step-by-step guide for onboarding new therapy clients.",
    video: "",

    content: [
      {
        type: "header",
        text: "Overview",
      },
      {
        type: "text",
        text: "This guide ensures all new clients are properly onboarded before beginning therapy, including intake, documentation, insurance verification, and platform setup.",
      },

      {
        type: "header",
        text: "Step 1: Initial Inquiry",
      },
      {
        type: "text",
        text: "Gather basic client information and determine service type (Virtual Platform vs Traditional) and payment method.",
      },

      {
        type: "header",
        text: "Step 2: Consultation (If Needed)",
      },
      {
        type: "text",
        text: "Consultations are optional but recommended for new or unknown clients. During consultation, document notes, confirm payment method, therapy format, and collect separate emails if the client is a minor.",
      },

      {
        type: "header",
        text: "Step 3: Required Documents",
      },
      {
        type: "text",
        text:
          "The following must be completed before the first session:\n\n" +
          "• Informed Consent for Therapy\n" +
          "• Notice of Privacy Practices\n" +
          "• Practice Policies\n" +
          "• Third Party Financial Responsibility Form\n" +
          "• Demographics Form\n" +
          "• Insurance Card\n" +
          "• VOB Confirmation",
      },

      {
        type: "header",
        text: "Credit Card Requirements",
      },
      {
        type: "text",
        text:
          "Required for:\n" +
          "• Commercial insurance clients\n" +
          "• Self-pay clients\n\n" +
          "Not required for:\n" +
          "• Medicaid\n• CareSource\n• Amerigroup\n\n" +
          "If required:\n" +
          "• CC Authorization\n• CC Information",
      },

      {
        type: "header",
        text: "Assessments",
      },
      {
        type: "text",
        text:
          "Assessments should ideally be completed before the first session:\n\n" +
          "• GAD-7\n• PHQ-9\n\n" +
          "Schedule:\n" +
          "• Week 0 (baseline)\n" +
          "• Week 4 reassessment\n" +
          "• Week 8 reassessment\n\n" +
          "Optional:\n" +
          "• WAI (Week 4)\n" +
          "• Recording consent (if needed)",
      },

      {
        type: "header",
        text: "Insurance Verification",
      },
      {
        type: "text",
        text:
          "Billing team must:\n" +
          "• Collect insurance card\n" +
          "• Verify benefits\n" +
          "• Confirm coverage\n" +
          "• Document VOB\n" +
          "• Confirm client responsibility\n" +
          "• Obtain prior authorization if needed\n\n" +
          "Therapy cannot begin until verification is complete unless self-pay.",
      },

      {
        type: "header",
        text: "Virtual Platform Clients",
      },
      {
        type: "text",
        text:
          "Clients must be registered on the Plutonic platform. Intake or therapist must notify the appropriate team member to complete registration.",
      },

      {
        type: "header",
        text: "Platform Registration",
      },
      {
        type: "text",
        text:
          "After registration, clients receive a welcome email with platform instructions and requirements.",
      },

      {
        type: "header",
        text: "Technical Requirements",
      },
      {
        type: "text",
        text:
          "Clients must use:\n" +
          "• Computer or laptop\n" +
          "• Google Chrome browser\n\n" +
          "Mobile devices are not supported.",
      },

      {
        type: "header",
        text: "Scheduling First Session",
      },
      {
        type: "text",
        text:
          "For virtual clients, the first session is scheduled by the platform team. Therapists should not schedule the initial session.",
      },

      {
        type: "header",
        text: "Traditional Therapy Workflow",
      },
      {
        type: "text",
        text:
          "If not using the virtual platform:\n\n" +
          "• Meet in person\n" +
          "OR\n" +
          "• Use SimplePractice video session link\n\n" +
          "No registration required, but appointment details must be correct.",
      },

      {
        type: "header",
        text: "Appointment Location Requirements",
      },
      {
        type: "text",
        text:
          "Therapists must select the correct location:\n\n" +
          "• Plutonic Virtual Platform\n" +
          "• Plutonic Telehealth\n" +
          "• Plutonic In Person (Gainesville / Norcross)\n\n" +
          "Therapist is responsible for correct location and service selection.",
      },
    ],
  },

  // ==== POLICY - Session Frequency
  {
    id: "therapy-frequency-policy",
    title: "Medical Necessity & Frequency",
    category: "policies",
    icon: "shield",
    description: "Guidelines for conducting more than one therapy session per week based on medical necessity.",
    video: "",

    content: [
      {
        type: "header",
        text: "Overview",
      },
      {
        type: "text",
        text:
          "Insurance providers may approve more than one therapy session per week if it is medically necessary. This requires proper documentation and justification to ensure the treatment is appropriate and not over-utilized.",
      },

      {
        type: "header",
        text: "When Increased Frequency is Allowed",
      },
      {
        type: "text",
        text:
          "Insurance companies may approve multiple sessions per week when:\n\n" +
          "• Treating a diagnosed mental health condition\n" +
          "• Preventing worsening symptoms\n" +
          "• Avoiding higher levels of care such as hospitalization\n\n" +
          "Common examples include high-intensity treatments like ERP (OCD) or CPT (PTSD), as well as acute mental health crises.",
      },

      {
        type: "header",
        text: "Medical Necessity Requirements",
      },
      {
        type: "text",
        text:
          "Providers must clearly document why one session per week is not sufficient. Documentation should include:\n\n" +
          "• Intake and treatment plan\n" +
          "• Progress notes\n" +
          "• Explanation of how increased frequency improves outcomes or prevents escalation",
      },

      {
        type: "header",
        text: "Insurance Review & Audits",
      },
      {
        type: "text",
        text:
          "Insurance companies may review or audit cases involving frequent sessions (2–3 per week). After a certain number of sessions (often 10–20), they may reassess whether continued high-frequency care is justified.",
      },

      {
        type: "header",
        text: "Limits & Coverage Considerations",
      },
      {
        type: "text",
        text:
          "• Some plans may limit the number of sessions per year\n" +
          "• Additional authorization may be required\n" +
          "• If denied, sessions may need to be paid out-of-pocket\n\n" +
          "Coverage varies significantly depending on the insurance provider and plan type.",
      },

      {
        type: "header",
        text: "Higher Levels of Care",
      },
      {
        type: "text",
        text:
          "If a client requires more than 2–3 sessions per week, insurers may determine that a higher level of care is needed, such as intensive outpatient or partial hospitalization programs.",
      },

      {
        type: "header",
        text: "Diagnosis Requirements",
      },
      {
        type: "text",
        text:
          "Insurance typically covers therapy for diagnosable mental health conditions (\"F codes\").\n\n" +
          "Frequent sessions are rarely covered for \"Z codes\" (general life stressors) unless tied to a clinical diagnosis.",
      },

      {
        type: "header",
        text: "Clinical Assessment Tools",
      },
      {
        type: "text",
        text:
          "Providers should use standardized tools such as:\n\n" +
          "• PHQ-9 (Depression)\n" +
          "• GAD-7 (Anxiety)\n\n" +
          "Results must be documented to support the need for increased session frequency.",
      },

      {
        type: "header",
        text: "Insurance Variability",
      },
      {
        type: "text",
        text:
          "Policies vary by provider. Some insurers (e.g., Blue Cross Blue Shield, Cigna) may be more flexible, while others require more extensive justification.\n\n" +
          "Medicaid and Medicare generally allow flexibility but require consistent documentation.",
      },

      {
        type: "header",
        text: "Client Responsibility",
      },
      {
        type: "text",
        text:
          "Clients should contact their insurance provider to confirm whether multiple sessions per week are covered.\n\n" +
          "They should notify Plutonic’s billing team and provide documentation if approval is granted. The billing team may follow up with the insurance provider as needed.",
      },
    ],
  },

  // ==== CPT CODES ====
  {
    id: "cpt-codes",
    title: "CPT Coding Tips",
    icon: "document",
    category: "policies",
    description: "Quick reference for therapy session durations and CPT codes.",
    video: "",

    content: [
      {
        type: "header",
        text: "Overview",
      },
      {
        type: "text",
        text:
          "All sessions must reflect accurate session duration and use the correct CPT code to ensure proper insurance billing and claim submission.",
      },

      {
        type: "header",
        text: "Common CPT Codes",
      },
      {
        type: "text",
        text:
          "90832 – Psychotherapy, 30 minutes (16–37 minutes)\n\n" +
          "90834 – Psychotherapy, 45 minutes (38–52 minutes)\n\n" +
          "90837 – Psychotherapy, 60 minutes (53+ minutes)",
      },
    ],
  }
];

export default helpSections;